import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { Equipment } from '@models/equipment.model';
import { PagedResponse } from '@models/response.model';

import { TableConfig } from '@app/shared/util/table-config.utils';
import { AppUtils } from '@app/shared/util/app.utils';

import { environment } from 'environments/environment';

import * as _ from 'lodash';
import { PartNumber } from "@models/partNumber.model";
import { map } from "rxjs/operators";

/**
 * Service class for handling CRUD operations on {@link Equipment} objects.
 */
@Injectable()
export class EquipmentService {
    // TODO gives 404 for the
    // filter-by-line url : http://atom-env.4uajeap82p.us-east-2.elasticbeanstalk.com/api/equipment/filtered-by-line?lineId=14
    static readonly EQUIPMENT_API_GET_PATH = environment.baseURL + '/api/equipment';
    static readonly EQUIPMENT_API_GET_FILTERED_PATH = environment.baseURL + '/api/equipment/filtered-by-line';
    static readonly EQUIPMENT_API_REPORT_DOWNLOAD_PATH = environment.baseURL + '/api/equipment/download';

    constructor(private http: HttpClient) {
    }

    getAllEquipment(tableConfig: TableConfig = null) {
        const params = AppUtils.buildPageableParams(tableConfig);
        return this.http.get<PagedResponse<Equipment>>(EquipmentService.EQUIPMENT_API_GET_PATH, {params: params})
            .pipe(map(this.mapEquipment));
    }

    getAllEquipmentNotInUse(tableConfig: TableConfig = null) {
        let params = AppUtils.buildPageableParams(tableConfig);
        params = params.set('inUse', '' + false);
        return this.http.get<PagedResponse<Equipment>>(EquipmentService.EQUIPMENT_API_GET_PATH, {params: params})
            .pipe(map(this.mapEquipment));
    }

    getAllEquipmentByLine(lines) {
        let params = new HttpParams();
        _.map(lines, 'id').forEach((item, index) => {
                params = params.set('EquipmentLinesFilter.LineIds[' + index + ']', item.toString())
            }
        );
        params = params.set('EquipmentLinesFilter.FilterType', `MatchLine`);
        return this.http.get<PagedResponse<Equipment>>(EquipmentService.EQUIPMENT_API_GET_PATH, {params: params})
            .pipe(map(this.mapEquipment));
    }

    getEquipmentByPartNumbers(partNumbers: PartNumber[]) {
        if (!partNumbers) {
            return;
        }
        let params = new HttpParams();
        partNumbers.forEach(item =>
            params = params.append('PN', item.id.toString())
        );

        return this.http.get<PagedResponse<Equipment>>(EquipmentService.EQUIPMENT_API_GET_PATH, {params: params})
            .pipe(map(this.mapEquipment));
    }

    mapEquipment = res => {
        return {
            equipment: res.items.map(equipment => new Equipment(equipment)),
            count: res.total
        };
    };

    createEquipment(equipment) {
        return this.http.post<Equipment>(EquipmentService.EQUIPMENT_API_GET_PATH, this.getCopyEquipment(equipment));
    }

    updateEquipment(equipment) {
        const copyEquipment = this.getCopyEquipment(equipment)
        return this.http.put<Equipment>(`${EquipmentService.EQUIPMENT_API_GET_PATH}/${copyEquipment.id}`, copyEquipment);
    }

    deleteEquipment(equipment) {
        return this.http.delete<Equipment>(`${EquipmentService.EQUIPMENT_API_GET_PATH}/${equipment.id}`);
    }

    getEquipmentByLine(lineId) {
        let params = new HttpParams();
        params = params.set('lineId', lineId);
        return this.http.get<any>(EquipmentService.EQUIPMENT_API_GET_FILTERED_PATH, {params: params});
    }

    getCopyEquipment(equipment) {
        const copyEquipment: any = {
            id: equipment.id,
            lineId: equipment.lineId,
            name: equipment.name,
            description: equipment.description,
            mesId: equipment.MESId,
            processTypes: _.map(equipment.processTypes, 'id')
        };
        return copyEquipment
    }

    downloadEquipReport(requestConfig: TableConfig = null) {
        const params = AppUtils.buildPageableParams(requestConfig);
        return this.http.get(EquipmentService.EQUIPMENT_API_REPORT_DOWNLOAD_PATH,
            {observe: 'response', params: params, responseType: 'blob'});
    }
}
